import { createFileRoute, Link } from "@tanstack/react-router";
import { useBeliefMap } from "#/api/beliefs";
import { useAuthUser } from "#/api/auth";
import { LogoMark } from "#/components/LogoMark";
import { Sparkles, ArrowLeft, Flame } from "lucide-react";

export const Route = createFileRoute("/beliefs")({
  component: BeliefsPage,
});

function BeliefsPage() {
  const { data: user } = useAuthUser();
  const { data, isPending, isError } = useBeliefMap();

  if (!user) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center px-6 text-center">
        <LogoMark size="xl" />
        <h1 className="mt-6 mb-3 font-serif text-3xl font-light text-foreground">
          Ineya
        </h1>
        <p className="mb-8 max-w-sm text-muted-foreground">
          Sign in to see the map of beliefs you've transformed.
        </p>
        <Link
          to="/login"
          className="inline-flex items-center gap-2 rounded-full bg-white/10 px-6 py-3 text-sm font-medium text-primary backdrop-blur-sm transition-all hover:bg-white/[0.14]"
        >
          Sign in
        </Link>
      </div>
    );
  }

  const map = data;

  return (
    <div className="min-h-screen px-6 pb-24 pt-28">
      <div className="mx-auto max-w-2xl">
        <Link
          to="/"
          className="mb-8 inline-flex items-center gap-2 text-xs text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to flow
        </Link>

        <header className="mb-12">
          <p className="mb-2 text-xs uppercase tracking-widest text-primary/80">
            Your belief constellation
          </p>
          <h1 className="font-serif text-4xl font-light leading-tight text-foreground md:text-5xl">
            The stories behind your fears
          </h1>
          <p className="mt-4 max-w-lg text-sm leading-relaxed text-muted-foreground">
            Every fear you've walked through left a belief behind it. One belief
            often produces many fears — when you see the pattern, dismantling it
            once dissolves all of them.
          </p>
        </header>

        {isPending ? (
          <div className="flex items-center gap-2 text-primary/80">
            <div className="h-2 w-2 animate-bounce rounded-full bg-primary" />
            <div className="h-2 w-2 animate-bounce rounded-full bg-primary [animation-delay:0.1s]" />
            <div className="h-2 w-2 animate-bounce rounded-full bg-primary [animation-delay:0.2s]" />
          </div>
        ) : isError ? (
          <p className="text-sm text-muted-foreground">
            We couldn't load your belief map. Please try again.
          </p>
        ) : !map || map.totalFears === 0 ? (
          <EmptyState />
        ) : (
          <>
            <Stats map={map} />
            <div className="mt-10 space-y-6">
              {map.clusters.map((cluster) => (
                <ClusterCard
                  key={cluster.limitingBelief.id}
                  cluster={cluster}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Stats({ map }: { map: NonNullable<ReturnType<typeof useBeliefMap>["data"]> }) {
  return (
    <div className="grid grid-cols-3 gap-3">
      <Stat label="Fears owned" value={map.totalFears} />
      <Stat label="Beliefs seen through" value={map.totalLimitingBeliefs} />
      <Stat label="New beliefs chosen" value={map.totalEmpoweringBeliefs} />
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4 text-center">
      <p className="font-serif text-3xl font-light text-primary">{value}</p>
      <p className="mt-1 text-[10px] uppercase tracking-widest text-muted-foreground">
        {label}
      </p>
    </div>
  );
}

function ClusterCard({
  cluster,
}: {
  cluster: NonNullable<ReturnType<typeof useBeliefMap>["data"]>["clusters"][number];
}) {
  const { limitingBelief, empoweringBelief, fears, fearCount } = cluster;
  const hero = fearCount >= 2;

  return (
    <article
      className={`rounded-3xl border p-6 backdrop-blur-sm ${
        hero
          ? "border-primary/30 bg-primary/[0.06]"
          : "border-white/10 bg-white/[0.04]"
      }`}
    >
      {hero && (
        <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-[10px] uppercase tracking-widest text-primary/90">
          <Flame className="h-3 w-3" />
          Behind {fearCount} fears
        </div>
      )}

      <div className="space-y-5">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground">
            The story
          </p>
          <p className="mt-1 font-serif text-xl text-foreground/60 line-through decoration-primary/40">
            {limitingBelief.text}
          </p>
        </div>

        {empoweringBelief && (
          <div>
            <p className="text-xs uppercase tracking-widest text-primary/80">
              You chose instead
            </p>
            <p className="mt-1 font-serif text-2xl text-primary">
              {empoweringBelief.text}
            </p>
            {empoweringBelief.mantra && (
              <p className="mt-2 font-serif text-base italic text-primary/80">
                "{empoweringBelief.mantra}"
              </p>
            )}
          </div>
        )}
      </div>

      <div className="mt-6 border-t border-white/10 pt-4">
        <p className="mb-2 text-[10px] uppercase tracking-widest text-muted-foreground">
          {fearCount === 1 ? "The fear that knocked" : `The ${fearCount} fears it wore`}
        </p>
        <ul className="space-y-1.5">
          {fears.map((fear) => (
            <li key={fear.id} className="text-sm leading-relaxed text-foreground/80">
              — {fear.text}
            </li>
          ))}
        </ul>
      </div>
    </article>
  );
}

function EmptyState() {
  return (
    <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-10 text-center">
      <div className="mb-5 flex justify-center">
        <LogoMark size="lg" />
      </div>
      <h2 className="mb-3 font-serif text-2xl font-light text-foreground">
        Your map begins with one fear
      </h2>
      <p className="mx-auto mb-8 max-w-md text-sm leading-relaxed text-muted-foreground">
        Walk through your first transformation and the constellation will start
        to take shape — each fear you own reveals the belief behind it.
      </p>
      <Link
        to="/"
        className="inline-flex items-center gap-2 rounded-full bg-white/10 px-6 py-3 text-sm font-medium text-primary backdrop-blur-sm transition-all hover:bg-white/[0.14]"
      >
        <Sparkles className="h-4 w-4" />
        Begin transformation
      </Link>
    </div>
  );
}
